import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { API_KEY } from "config";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { Timers } from "./timers";
import { getAccessTokenFromLocalStorage } from "./auth";
import isTokenExpired from "./check_token_expiration";

const RETRY_CONFIG = Object.freeze({
  retries: 0, // リトライ回数。
  delayMills: 100, // リトライ前に設ける遅滞時間(ミリ秒数)。Lambdaのタイムアウト(デフォルトは3秒)に留意。
});

/**
 * リトライ設定
 */
export type RetryConfig = {
  retries?: number;
  delayMills?: number;
};

/**
 * 認証設定
 */
export type AuthConfig = {
  useToken?: boolean;
  useFormData?: boolean;
};

/**
 * HTTPクライアント
 */
export default class Http {
  static #default = new Http({});

  /**
   * リクエスト設定
   */
  readonly #requestConfig: AxiosRequestConfig<any> = {};

  /**
   * コンストラクタ
   * @param requestConfig
   */
  constructor(requestConfig: AxiosRequestConfig<any>) {
    this.#requestConfig = Object.freeze(requestConfig);
  }

  /**
   * デフォルト設定のHTTPクライアントを返す
   */
  static get default() {
    return Http.#default;
  }

  /**
   * 認証情報を含んだHttpを取得
   * @param authConfig 認証情報設定
   * @returns HTTPクライアント
   */
  alterAuth(authConfig: AuthConfig) {
    const altered = { ...this.#requestConfig };
    // altered.headers = {...altered.headers, ...{'x-api-key': (Auth.apikey())}};
    if (authConfig.useToken) {
      altered.headers = { ...altered.headers, ...{ Authorization: "" } };
      altered.headers.Authorization = `Bearer ${getAccessTokenFromLocalStorage()}`;
    }
    if (authConfig.useFormData) {
      altered.headers = { ...altered.headers, ...{ "Content-Type": "multipart/form-data" } };
    }

    return new Http(altered);
  }

  /**
   * フォームデータに変換する
   * @param obj 変換するオブジェクト
   */
  static convertObjectToFormData(obj: Record<string, any>): FormData {
    const formData = new FormData();

    Object.entries(obj).forEach(([key, value]) => {
      if (value instanceof File) {
        // ファイルの場合
        formData.append(key, value, value.name);
      } else if (Array.isArray(value)) {
        // 配列の場合
        value.forEach((item) => {
          formData.append(key, item);
        });
      } else {
        // その他の場合
        formData.append(key, value.toString());
      }
    });

    return formData;
  }

  /**
   * Cognito認証とAPIキーを使う
   */

  /*
  async useCognitoAuth() {
    let idToken;
    try {
      await fetchAuthSession()
        .then(session => {
           idToken = session.tokens?.idToken?.toString();
      });
    }
    catch (e) { console.log(e); }

    const headers = {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'x-api-key': API_KEY
      },
    };
    const config = { ...this.#requestConfig, ...headers };
    return new Http(config);
  }
  */

  /**
   *
   * @param response
   */
  static handleError(response: AxiosResponse & { message?: string }): void {
    const { status, data } = response || {};

    switch (status) {
      case 400:
      case 404:
      case 500:
        if (data?.message) {
          Notify?.setNotification?.({
            show: true,
            title: data?.message,
            message: "",
            titleColor: "var(--error)",
          });
        }
        break;
      case 401:
      case 403: // ユーザー認証不正も403で返ってくるためリログとする。
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
        break;
      case 504:
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.GATEWAY_TIMEOUT.TITLE,
          message: ERROR_MESSAGE.GATEWAY_TIMEOUT.MESSAGE,
        });
        break;
      default:
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.DEFAULT.TITLE,
          message: ERROR_MESSAGE.DEFAULT.MESSAGE,
        });
        break;
    }
  }

  /**
   * リフレッシュトークンを再発行
   * @param url url
   */
  static async reissueRefreshToken(url: string) {
    // Skip token checking for login and refresh token APIs
    const isLoginApi = url === "/mst/user/auth/login";
    const isRefreshTokenApi = url === "/mst/user/auth/update/token";
    const shouldCheckToken = !isLoginApi && !isRefreshTokenApi;

    if (shouldCheckToken) {
      // Check if token is still valid (not expired)
      if (!isTokenExpired()) {
        // Token is still valid, refresh token before calling API
        const { default: refreshToken } = await import("util/tokenRefresh");
        await refreshToken();

        // Wait for any ongoing token refresh to complete before making API calls
        const { waitForTokenRefresh } = await import("util/tokenRefresh");
        await waitForTokenRefresh();
      }
      // If token has expired, continue to call API with old token
      // Backend will return 403, then handleError will show notification
      // At that time, Notify component has already been initialized
    }
  }

  /**
   * GETリクエスト送信
   * @param url 送信先URL
   * @param optionalConfig 追加設定
   * @returns HTTPレスポンス
   */
  async get<T = any>(
    url: string,
    optionalConfig?: AxiosRequestConfig<any>,
  ): Promise<AxiosResponse<T>> {
    await Http.reissueRefreshToken(url); // リフレッシュトークン再発行
    const config = { ...this.#requestConfig, ...optionalConfig };
    try {
      return await axios.get<T>(url, config);
    } catch (error: any) {
      Http.handleError(error?.response);
      throw error;
    }
  }

  /**
   * PUTリクエスト送信
   * @param url 送信先URL
   * @param data 送信するデータ
   * @param optionConfig 追加設定
   * @returns HTTPレスポンス
   */
  async put<T = any>(
    url: string,
    data: any,
    optionConfig?: AxiosRequestConfig<any>,
  ): Promise<AxiosResponse<T>> {
    await Http.reissueRefreshToken(url); // リフレッシュトークン再発行
    const config = { ...this.#requestConfig, ...optionConfig };
    console.log(url, data);
    return Http.#exec(() => axios.put<T>(url, data, config), this.#retryConfig);
  }

  readonly #retryConfig: RetryConfig = {};

  /**
   * HTTPリクエストを実行します
   * @param callback 実行するリクエスト処理
   * @param retryConfig リトライ設定
   * @returns HTTPレスポンス
   */
  static async #exec(callback: () => Promise<AxiosResponse<any>>, retryConfig: RetryConfig) {
    const maxTries = retryConfig.retries ?? RETRY_CONFIG.retries;
    const delayMillis = retryConfig.delayMills ?? RETRY_CONFIG.delayMills;
    let tried = 0;
    while (true) {
      try {
        // 最大トライ数までリトライ
        // eslint-disable-next-line no-await-in-loop
        return await callback();
      } catch (error: any) {
        tried += 1;
        if (tried >= maxTries) {
          this.handleError(error?.response);
          throw error;
        }
        if (!this.#isRetriableError(error)) {
          this.handleError(error?.response);
          throw error;
        }
        // eslint-disable-next-line no-await-in-loop
        await Timers.delay(delayMillis);
      }
    }
  }

  /**
   * POSTリクエストを送信。
   *
   * - 保持するリトライ設定、リクエスト設定に従って処理を実施。
   * - デフォルトでは、指定のデータをJSONに変換して送信。
   * @example
   * @param url HTTPリクエストの送信先URL。
   * @param data リクエストボディとして送信するデータ。
   * @param optionConfig この処理にのみ適用する追加のリクエスト設定。
   * @returns HTTPレスポンス。
   */
  async post<T = any>(
    url: string,
    data: any,
    optionConfig?: AxiosRequestConfig<any>,
  ): Promise<AxiosResponse<T>> {
    await Http.reissueRefreshToken(url); // リフレッシュトークン再発行
    const config = { ...this.#requestConfig, ...optionConfig };
    console.log(url, data);
    return Http.#exec(() => axios.post<T>(url, data, config), this.#retryConfig);
  }

  /**
   * リトライ失敗時エラー
   * @param error エラー内容
   * @returns リトライするエラーの場合true
   */
  static #isRetriableError(error: unknown) {
    if (!axios.isAxiosError(error)) {
      return false;
    }
    const isRequestTimeout = error.code === "ECONNABORTED";
    if (isRequestTimeout) {
      return false;
    }
    const httpStatus = error.response?.status;
    if (httpStatus === 503) {
      // 503 Service Unavailable
      return true;
    }
    return false;
  }
}
